<template>
  <div class="relative flex justify-center">
    <div class="audio-record flex flex-col items-center">
      <div
        class="relative flex items-center justify-center w-24 h-24 p-1.5  transition-all duration-500 delay-200 transform-gpu"
        :class="{ 'p-3.5': isLoading }"
        @click.stop="handleRecordClick"
      >
        <div v-show="isLoading" class="btn-bg"></div>
        <div class="record_button" :class="{ record_button_disabled: isDisabled }">
          <Transition name="fade" mode="out-in">
            <img v-if="!isRecording" class="w-7" :src="recorderBtnIcon" alt="" />
            <div v-else class="relative w-5 h-5 bg-white rounded-t rounded-b"></div>
          </Transition>
        </div>
      </div>

      <div class="record-tips">
        <span>{{ recorderTips }}</span>
        <span v-if="isTipsBtnShow" class="tips-btn" @click="turnToHelp">查看指引</span>
      </div>
    </div>
  </div>
</template>
<script lang="ts">
import { Vue, Component, Prop } from 'vue-property-decorator';
import Clock from '@/utils/clock';
import upload from './upload';
import IconMic from './assets/mic-icon.svg';
import IconUpload from './assets/upload-icon.svg';
import IconRetry from './assets/retry-icon.svg';
import { namespace } from 'vuex-class';
import { nanoid } from 'nanoid';

import UPSERT_VOICE from './graphql/upsertCourseLearningVoice';
import { Environment } from '@/store/modules/common/types';

const common = namespace('common');

enum RecorderState {
  DEFAULT = 'DEFAULT',
  RECORDING = 'RECORDING',
  UPLOADING = 'UPLOADING',
  FINISHED = 'FINISHED',
  DISABLED = 'DISABLED',
  NOTALLOW = 'NOTALLOW',
  NODEVICE = 'NODEVICE',
  UPLOADFAIL = 'UPLOADFAIL',
  UPSERTFAIL = 'UPSERTFAIL'
}

enum RecorderError {
  NOTALLOW = 'Permission|NotAllowed',
  NODEVICE = 'NotFound|DevicesNotFound'
}

@Component({})
export default class AudioRecord extends Vue {
  @Prop() isReRecord!: boolean;
  @Prop() title!: string;
  @common.State('environment') environment!: Environment;
  @common.State('version') version: string;

  recorderState = RecorderState.DEFAULT;

  clock = new Clock();
  recorder?: MediaRecorder;
  chunks: any[] = [];

  file: File;

  get isRecording() {
    return this.recorderState === RecorderState.RECORDING;
  }
  get isUploading() {
    return this.recorderState === RecorderState.UPLOADING;
  }
  get isLoading() {
    return this.isRecording || this.isUploading;
  }
  get isDisabled() {
    return this.recorderState === RecorderState.DISABLED;
  }
  get isNotAllow() {
    return this.recorderState === RecorderState.NOTALLOW;
  }
  get isNoDevice() {
    return this.recorderState === RecorderState.NODEVICE;
  }
  get isUploadFail() {
    return this.recorderState === RecorderState.UPLOADFAIL;
  }
  get isUpsertFail() {
    return this.recorderState === RecorderState.UPSERTFAIL;
  }
  get isFinished() {
    return this.recorderState === RecorderState.FINISHED;
  }

  get isTipsBtnShow() {
    return this.isNotAllow || this.isNoDevice || this.isDisabled;
  }

  get recorderBtnIcon() {
    let icon = IconMic;

    if (this.isUploadFail || this.isUpsertFail) {
      icon = IconRetry;
    } else if (this.isUploading) {
      icon = IconUpload;
    }

    return icon;
  }

  get recorderTips() {
    if (this.isRecording) {
      return this.clock.dial_mmss;
    } else if (this.isUploading) {
      return '上传中...';
    } else if (this.isFinished) {
      return '上传成功！';
    } else if (this.isDisabled) {
      return '当前浏览器不支持麦克风，请使用Chrome，';
    } else if (this.isNoDevice) {
      return '电脑麦克风未连接或未打开，';
    } else if (this.isNotAllow) {
      return '浏览器使用麦克风权限未打开，';
    } else if (this.isUploadFail || this.isUpsertFail) {
      return '保存录音失败，请重试。';
    } else {
      return '点按录音';
    }
  }

  async handleRecordClick() {
    if (this.isDisabled || this.isUploading || this.isFinished) {
      return;
    }

    if (this.isUploadFail) {
      this.recorderState = RecorderState.UPLOADING;
      await this.uploadFile();
    } else if (this.isRecording) {
      // to stop
      this.recorder!.stop();
    } else {
      // to start
      if (!this.recorder) {
        this.recorder = await this.newRecord();
      }
      this.chunks = [];
      if (this.recorder) {
        console.log(this.recorder.state);
        this.recorderState = RecorderState.RECORDING;
        this.recorder.start();
        this.clock.start();
      }
    }
  }

  async newRecord() {
    // NOTE 需要在localhost或https协议下才能获取navigator.mediaDevices
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      if (stream) {
        console.log('授权成功！');
        this.recorderState = RecorderState.RECORDING;
        const recorder = new MediaRecorder(stream);
        recorder.ondataavailable = (e) => {
          this.chunks.push(e.data);
        };
        recorder.onstop = this.onstop;
        return recorder;
      }
    } catch (error) {
      const nError = error as Error;
      console.log(nError);
      const state = this.handleMediaError(nError);
      if (state) {
        this.recorderState = state;
      } else {
        console.log(nError);
        this.$Message.error({
          content: nError.message || '录音出现错误，请刷新重试'
        });
        this.recorderState = RecorderState.DEFAULT;
      }
    }
  }
  handleMediaError(err: Error) {
    console.log(err.message);
    if (new RegExp(RecorderError.NOTALLOW).test(err.message)) {
      return RecorderState.NOTALLOW;
    } else if (new RegExp(RecorderError.NODEVICE).test(err.message)) {
      return RecorderState.NODEVICE;
    }
  }

  async onstop() {
    this.clock.pause();
    this.clock.clear();

    this.recorderState = RecorderState.UPLOADING;

    const blob = new Blob(this.chunks, {
      type: 'audio/ogg; codecs=opus'
    });

    const name = nanoid() + '.ogg';
    const file = new File([blob], name, {
      type: blob.type,
      lastModified: Number(new Date())
    });
    this.file = file;
    console.log(file);
    await this.uploadFile();
  }

  async uploadFile() {
    try {
      const filePath = await upload.put(this.file);
      await this.upsertVoice(filePath);
      this.$emit('file-change', filePath);
      this.recorderState = RecorderState.FINISHED;
    } catch (error) {
      if ((error as Error).message === RecorderState.UPSERTFAIL) {
        this.$Message.error({ content: '保存文件地址出错！' });
        this.recorderState = RecorderState.UPSERTFAIL;
      } else {
        this.$Message.error({ content: '上传到OSS出错！' });
        this.recorderState = RecorderState.UPLOADFAIL;
      }
    }
  }

  async upsertVoice(url: string) {
    try {
      const courseId =
        this.environment && this.environment.kind === 'public-preview'
          ? this.environment.sharedKey
          : '';
      if (!courseId) return;

      await this.$getClemanClient().apollo.mutate({
        mutation: UPSERT_VOICE,
        variables: {
          url,
          title: this.title,
          courseVersion: this.version,
          courseId
        }
      });
    } catch (error) {
      throw Error(RecorderState.UPSERTFAIL);
    }
  }

  turnToHelp() {
    window.open('https://forchangesz.feishu.cn/docx/CDJVdkzUWoeUURxGSGUcM4WQnCh');
  }

  mounted() {
    if (!navigator?.mediaDevices?.getUserMedia) {
      this.recorderState = RecorderState.DISABLED;
    } else {
      if (this.isReRecord) {
        this.handleRecordClick();
      }
    }
  }
}
</script>

<style scoped lang="scss">
.audio-record {
  position: relative;
}
.btn-bg {
  @apply absolute rounded-full pointer-events-none transform-gpu;
  background-color: rgba(116, 80, 245, 0.1);
  animation: Recording 2s ease-in-out alternate infinite;
}
.record_button {
  @apply relative flex justify-center items-center cursor-pointer w-full h-full rounded-full;
  background: linear-gradient(95.22deg, #524eff 6.46%, #8b8eff 91.58%);
}
.record_button_disabled {
  @apply cursor-not-allowed opacity-40;
}

.record-tips {
  @apply mt-3 text-sm text-center;
  color: #666666;
}
.tips-btn {
  @apply cursor-pointer hover:opacity-80;
  color: #433eff;
}

.retry-btn {
  top: 827px;
  left: 385px;
  width: 84px;
  height: 32px;
  color: #1a051d;
  background: #ffffff;
  border: 1.2px solid #c2c2c2;
  border-radius: 4px;
  &:hover {
    color: #433eff;
    border-color: #433eff;
  }
}

@keyframes Recording {
  0% {
    width: 70%;
    height: 70%;
    opacity: 1;
  }
  90% {
    width: 100%;
    height: 100%;
    opacity: 1;
  }
  100% {
    width: 100%;
    height: 100%;
    opacity: 1;
  }
}
</style>
